import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';

const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';

// ── PDF Viewer using PDF.js (works on all browsers including mobile) ───────────
function PDFViewer({ examId }) {
  const wrapperRef   = useRef(null);
  const containerRef = useRef(null);
  const [status, setStatus]   = useState('Fetching question paper...');
  const [errorMsg, setErrorMsg] = useState('');
  const [pdfUrl, setPdfUrl]   = useState('');   // fallback direct-open URL

  useEffect(() => {
    let cancelled = false;
    // Build a direct URL for the "Open PDF" fallback button — request a
    // short-lived, exam-scoped token so the user's bearer JWT never ends up
    // in the URL / browser history / referrer.
    const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    API.get(`/exams/${examId}/pdf-access`)
      .then(r => {
        if (cancelled) return;
        const pt = r.data?.pdf_token;
        if (pt) setPdfUrl(`${base}/exams/${examId}/pdf?pdf_token=${encodeURIComponent(pt)}`);
      })
      .catch(() => { /* fallback link just won't be available */ });

    async function render() {
      try {
        // 1. Fetch PDF bytes via configured API (handles auth + CORS)
        const response = await API.get(`/exams/${examId}/pdf`, { responseType: 'arraybuffer' });
        if (cancelled) return;

        // 2. Wait up to 6 s for PDF.js CDN script to be ready
        let waited = 0;
        while (!window.pdfjsLib && waited < 6000) {
          await new Promise(r => setTimeout(r, 300));
          waited += 300;
        }
        if (!window.pdfjsLib) throw new Error('PDF.js library failed to load.');
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;

        setStatus('Rendering pages...');
        // Convert ArrayBuffer → Uint8Array for compatibility with older PDF.js / Chrome
        const pdfData = new Uint8Array(response.data);
        const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
        if (cancelled) return;

        const container = containerRef.current;
        const wrapper   = wrapperRef.current;
        if (!container || !wrapper) return;
        container.innerHTML = '';

        // 3. Wait one frame so the DOM is laid out before measuring width
        await new Promise(r => requestAnimationFrame(r));
        const containerWidth = wrapper.getBoundingClientRect().width
          || wrapper.clientWidth
          || window.innerWidth
          || 600;

        // Render at device pixel ratio so text is crisp on retina / mobile HiDPI screens
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = containerWidth - 16; // logical CSS pixels (16 = padding)

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return;
          const page   = await pdf.getPage(pageNum);
          const baseVP = page.getViewport({ scale: 1 });
          const scale  = (cssWidth / baseVP.width) * dpr;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width  = viewport.width;
          canvas.height = viewport.height;
          canvas.style.cssText =
            `display:block;width:${cssWidth}px;height:${Math.round(viewport.height / dpr)}px;` +
            'margin:0 auto 8px;box-shadow:0 2px 8px rgba(0,0,0,0.4);background:#fff;border-radius:4px;';

          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (pageNum === 1) setStatus('');
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e.response?.data?.detail || e.message || 'Failed to load question paper.');
          setStatus('');
        }
      }
    }
    render();
    return () => { cancelled = true; };
  }, [examId]);

  return (
    <div ref={wrapperRef}
      style={{ flex: 1, width: '100%', minHeight: 0, overflowY: 'scroll', overflowX: 'hidden',
               background: '#374151', padding: 8, boxSizing: 'border-box',
               WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      {status && (
        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40, fontSize: 14 }}>
          {status}
        </div>
      )}
      {errorMsg && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ color: '#f87171', fontSize: 14, marginBottom: 16 }}>{errorMsg}</div>
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 10, background: '#2554b8', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
              Open Question Paper ↗
            </a>
          )}
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%' }} />
    </div>
  );
}
const FLAG_COOLDOWN = 6000;        // ms between same-category flags
const FACE_INTERVAL = 1500;        // ms between face checks
const SOUND_INTERVAL = 3000;       // ms between sound checks
const SOUND_THRESHOLD = 38;        // average frequency amplitude to flag
const PAPER_VERIFY_EVERY = 5 * 60 * 1000; // every 5 min prompt student to show paper
const PAPER_VERIFY_TIMEOUT = 30000;        // 30s to acknowledge

function fmtTime(s) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export default function ExamRoom({ examData, onComplete }) {
  const { id: examId, title, subject, duration_minutes } = examData;

  const [sessionId, setSessionId]       = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [sessionTry, setSessionTry]     = useState(0);
  const [timeLeft, setTimeLeft]         = useState(duration_minutes * 60);
  const [flags, setFlags]               = useState([]);
  const [procStatus, setProcStatus]     = useState('Starting camera...');
  const [procColor, setProcColor]       = useState('#64748b');
  const [camError, setCamError]         = useState(false);
  const [modelsReady, setModelsReady]   = useState(false);
  const [loadingMsg, setLoadingMsg]     = useState('Loading AI face models...');
  const [showFlags, setShowFlags]       = useState(false);
  const [paperPrompt, setPaperPrompt]   = useState(false);
  const [completed, setCompleted]       = useState(false);
  const [isMobile]                      = useState(() => window.innerWidth < 768);

  const videoRef      = useRef(null);
  const canvasRef     = useRef(null);
  const streamRef     = useRef(null);
  const procRef       = useRef(null);
  const timerRef      = useRef(null);
  const paperRef      = useRef(null);
  const sessionRef    = useRef(null);
  const lastFlagTime  = useRef({});
  const analyserRef   = useRef(null);
  const audioCtxRef   = useRef(null);

  // ── Add flag with per-category cooldown ──────────────────────────────────────
  const addFlag = useCallback(async (reason, category) => {
    const now = Date.now();
    const key = category || reason;
    if (now - (lastFlagTime.current[key] || 0) < FLAG_COOLDOWN) return;
    lastFlagTime.current[key] = now;

    const flag = { reason, ts: new Date().toLocaleTimeString() };
    setFlags(prev => [flag, ...prev.slice(0, 49)]); // keep last 50

    if (sessionRef.current) {
      try { await API.post(`/exams/sessions/${sessionRef.current}/flags`, { reason }); } catch (_) {}
    }
  }, []);

  // ── Start session on mount (and on manual retry) ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setSessionError('');
    API.post(`/exams/${examId}/sessions`)
      .then(r => {
        if (cancelled) return;
        sessionRef.current = r.data.session_id;
        setSessionId(r.data.session_id);
      })
      .catch((e) => {
        if (cancelled) return;
        // Don't leave the student stranded on "Preparing exam session…" forever.
        // The api interceptor already refreshes CSRF/token and retries once; if
        // it still failed, surface a clear message with a Retry button.
        setSessionError(
          e?.response?.data?.detail ||
          'Could not start the exam session. Check your connection and tap Retry.'
        );
      });
    return () => { cancelled = true; };
  }, [examId, sessionTry]);


  // ── Load face-api models ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!window.faceapi) {
      setLoadingMsg('');
      setModelsReady(true);
      return;
    }
    (async () => {
      try {
        setLoadingMsg('Loading face detector...');
        await window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        setLoadingMsg('Loading landmark model...');
        await window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        setModelsReady(true);
        setLoadingMsg('');
      } catch (_) {
        setModelsReady(true);
        setLoadingMsg('');
      }
    })();
  }, []);

  // ── Start camera after models ready ──────────────────────────────────────────
  useEffect(() => {
    if (!modelsReady) return;
    startCamera();
    return () => stopAll();
  }, [modelsReady]); // eslint-disable-line

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onplay = startFaceLoop;
      }
      setProcStatus('Monitoring');
      setProcColor('#16a34a');
    } catch (_) {
      setCamError(true);
      setProcStatus('Camera denied');
      setProcColor('#dc2626');
      addFlag('Camera access denied', 'cam_denied');
    }
    startSoundDetection();
  };

  const stopAll = () => {
    if (procRef.current) clearInterval(procRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (audioCtxRef.current) try { audioCtxRef.current.close(); } catch (_) {}
  };

  // ── Sound detection ───────────────────────────────────────────────────────────
  const startSoundDetection = async () => {
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(ms).connect(analyser);
      analyserRef.current = analyser;

      setInterval(() => {
        if (!analyserRef.current) return;
        const arr = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(arr);
        const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
        if (avg > SOUND_THRESHOLD) addFlag('Suspicious sound detected nearby', 'sound');
      }, SOUND_INTERVAL);
    } catch (_) { /* audio permission denied — skip */ }
  };

  // ── Face detection loop ───────────────────────────────────────────────────────
  const startFaceLoop = () => {
    if (!window.faceapi) return;
    // Consecutive-frame counters — only flag after N back-to-back detections
    // to avoid one-frame glitches triggering alerts
    let noFaceCount   = 0;
    let lookAwayCount = 0;

    procRef.current = setInterval(async () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      // Wait until video is playing and has real dimensions
      if (!video || !canvas || video.readyState < 2 || video.videoWidth === 0) return;

      const displaySize = { width: video.clientWidth || 260, height: video.clientHeight || 180 };
      window.faceapi.matchDimensions(canvas, displaySize);

      try {
        const detections = await window.faceapi
          .detectAllFaces(video, new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }))
          .withFaceLandmarks();

        // Draw detection overlay using display (CSS) size
        const resized = window.faceapi.resizeResults(detections, displaySize);
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        window.faceapi.draw.drawDetections(canvas, resized);

        if (detections.length === 0) {
          noFaceCount++;
          lookAwayCount = 0;
          // Require 2 consecutive misses (~3 s) before flagging
          if (noFaceCount >= 2) {
            setProcStatus('No face detected!');
            setProcColor('#dc2626');
            addFlag('Student not visible in camera', 'no_face');
          }
        } else if (detections.length > 1) {
          noFaceCount = 0;
          lookAwayCount = 0;
          setProcStatus('Multiple people!');
          setProcColor('#dc2626');
          addFlag('Multiple people detected in frame', 'multi_face');
        } else {
          noFaceCount = 0;
          const lm = detections[0].landmarks;
          if (!lm) {
            setProcStatus('Monitoring ✓');
            setProcColor('#16a34a');
            return;
          }
          const leftEye  = lm.getLeftEye();
          const rightEye = lm.getRightEye();
          const nose     = lm.getNose();

          const noseX       = nose[3].x;
          const noseY       = nose[3].y;
          const leftEyeX    = leftEye.reduce((s, p) => s + p.x, 0) / leftEye.length;
          const rightEyeX   = rightEye.reduce((s, p) => s + p.x, 0) / rightEye.length;
          const eyeAvgY     = (leftEye[0].y + rightEye[0].y) / 2;
          const faceH       = lm.positions[8].y - lm.positions[27].y;

          const hRatio      = Math.abs(noseX - leftEyeX) / (Math.abs(noseX - rightEyeX) || 1);
          const lookingDown = (noseY - eyeAvgY) > faceH * 0.35;

          // Relaxed thresholds: 2.5 / 0.4 (was 2.2 / 0.45) — reduces false look-away flags
          if (!lookingDown && (hRatio > 2.5 || hRatio < 0.4)) {
            lookAwayCount++;
            // Require 2 consecutive checks before flagging
            if (lookAwayCount >= 2) {
              setProcStatus('Looking away!');
              setProcColor('#f59e0b');
              addFlag('Student looking sideways from exam area', 'look_away');
            }
          } else {
            lookAwayCount = 0;
            setProcStatus(lookingDown ? 'Writing ✓' : 'Monitoring ✓');
            setProcColor('#16a34a');
          }
        }
      } catch (_) { /* detection error — skip this frame */ }
    }, FACE_INTERVAL);
  };

  // ── Countdown timer ───────────────────────────────────────────────────────────
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleComplete(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line

  // ── Tab / window switch detection ─────────────────────────────────────────────
  useEffect(() => {
    const onHide = () => { if (document.hidden) addFlag('Student switched tab or window', 'tab_switch'); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [addFlag]);

  // ── Paper verification prompt every 5 min ─────────────────────────────────────
  useEffect(() => {
    paperRef.current = setInterval(() => {
      setPaperPrompt(true);
      setTimeout(() => {
        setPaperPrompt(v => {
          if (v) addFlag('Student did not show answer sheet for verification', 'paper_verify');
          return false;
        });
      }, PAPER_VERIFY_TIMEOUT);
    }, PAPER_VERIFY_EVERY);
    return () => clearInterval(paperRef.current);
  }, [addFlag]);

  // ── Complete exam ─────────────────────────────────────────────────────────────
  const handleComplete = async (auto = false) => {
    clearInterval(timerRef.current);
    stopAll();
    if (sessionRef.current) {
      try { await API.patch(`/exams/sessions/${sessionRef.current}/complete`); } catch (_) {}
    }
    setCompleted(true);
  };

  // ── Completed screen ──────────────────────────────────────────────────────────
  if (completed) {
    return (
      <div style={{ position:'fixed', inset:0, background:'#fff', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', zIndex:5000, fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
        <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
        <h2 style={{ fontSize:26, fontWeight:800, color:'#0f1f45', marginBottom:8 }}>Exam Submitted!</h2>
        <p style={{ color:'#64748b', fontSize:14, marginBottom:8 }}>Your session has been recorded and saved.</p>
        {flags.length > 0 && (
          <p style={{ color:'#f59e0b', fontSize:13, marginBottom:24 }}>⚠️ {flags.length} alert{flags.length !== 1 ? 's' : ''} were logged during your session.</p>
        )}
        <button onClick={onComplete}
          style={{ padding:'14px 44px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, fontSize:15, cursor:'pointer', boxShadow:'0 6px 20px rgba(37,84,184,0.4)' }}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const timerWarn = timeLeft < 300;
  const timerCrit = timeLeft < 60;

  return (
    <div style={{ position:'fixed', inset:0, background:'#0f1f45', display:'flex', flexDirection:'column', zIndex:5000, overflow:'hidden', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', paddingTop:'max(10px, env(safe-area-inset-top))', background:'rgba(0,0,0,0.35)', borderBottom:'1px solid rgba(255,255,255,0.1)', flexShrink:0, gap:8, flexWrap:'wrap' }}>
        <div style={{ minWidth:0 }}>
          <div style={{ fontWeight:800, fontSize:14, color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:200 }}>{title}</div>
          {subject && <div style={{ fontSize:11, color:'#94a3b8' }}>{subject}</div>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          {/* Timer */}
          <div style={{
            padding:'6px 14px', borderRadius:10, fontWeight:800, fontSize:18,
            fontVariantNumeric:'tabular-nums', minWidth:76, textAlign:'center',
            background: timerCrit ? '#dc2626' : timerWarn ? '#f59e0b' : 'rgba(255,255,255,0.12)',
            color: timerCrit || timerWarn ? '#fff' : '#e2e8f0',
            animation: timerCrit ? 'pulse 1s infinite' : 'none',
          }}>
            ⏱ {fmtTime(timeLeft)}
          </div>
          {/* Flags badge */}
          {flags.length > 0 && (
            <button onClick={() => setShowFlags(v => !v)} style={{ padding:'6px 10px', borderRadius:8, background:'rgba(239,68,68,0.2)', color:'#fca5a5', fontWeight:700, fontSize:12, border:'1px solid rgba(239,68,68,0.3)', cursor:'pointer' }}>
              ⚠️ {flags.length}
            </button>
          )}
          {/* Submit */}
          <button onClick={() => { if (window.confirm('Submit your exam now? This action cannot be undone.')) handleComplete(); }}
            style={{ padding:'8px 16px', borderRadius:10, background:'#dc2626', color:'#fff', fontWeight:800, fontSize:13, border:'none', cursor:'pointer', boxShadow:'0 2px 8px rgba(220,38,38,0.4)' }}>
            Submit ✓
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex:1, display:'flex', flexDirection: isMobile ? 'column' : 'row', overflow:'hidden', position:'relative' }}>

        {/* PDF Viewer — on mobile takes full height; camera floats over it */}
        <div style={{
          flex: isMobile ? '1 1 0' : '0 0 75%',
          overflow: 'hidden',
          minHeight: isMobile ? 0 : 'auto',
          display: 'flex', flexDirection: 'column',
          // on mobile add bottom padding so content isn't hidden behind the camera overlay
          paddingBottom: isMobile ? 280 : 0,
        }}>
          {sessionId ? (
            <PDFViewer examId={examId} />
          ) : sessionError ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ color: '#f87171', fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>{sessionError}</div>
              <button
                onClick={() => { setSessionError(''); setSessionTry(n => n + 1); }}
                style={{ padding: '10px 26px', borderRadius: 10, background: '#2554b8', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                ↻ Retry
              </button>
            </div>
          ) : (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40, fontSize: 14 }}>
              Preparing exam session…
            </div>
          )}
        </div>

        {/* Proctoring panel — always visible; floats over PDF on mobile */}
        <div style={{
          ...(isMobile ? {
            position: 'absolute', bottom: 0, left: 0, right: 0,
            zIndex: 20, paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          } : {
            flexShrink: 0, width: 260,
          }),
          height: isMobile ? 280 : 'auto',
          background:'#0f172a',
          borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.07)',
          borderTop: isMobile ? '2px solid rgba(255,255,255,0.15)' : 'none',
          display:'flex', flexDirection:'column', padding:10, gap:8, overflow:'hidden',
        }}>
            {/* Status pill */}
            <div style={{ textAlign:'center', padding:'4px 8px', borderRadius:20, background:'rgba(255,255,255,0.05)', fontSize:11, fontWeight:700, color:procColor }}>
              ● {procStatus}
            </div>

            {/* Camera feed */}
            <div style={{ position:'relative', background:'#000', borderRadius:10, overflow:'hidden', height: isMobile ? 200 : 180, flexShrink:0 }}>
              {camError ? (
                <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#ef4444', fontSize:11, textAlign:'center', padding:8 }}>
                  📵 Camera access denied<br />Monitoring limited
                </div>
              ) : (
                <>
                  <video ref={videoRef} autoPlay muted playsInline style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                  <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} />
                </>
              )}
            </div>

            {loadingMsg && <p style={{ color:'#64748b', fontSize:10, textAlign:'center', margin:0 }}>{loadingMsg}</p>}

            {/* Flags list — visible on desktop always, mobile only when toggled */}
            {!isMobile && flags.length > 0 && (
              <div style={{ flex:1, overflow:'auto' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', marginBottom:4 }}>⚠️ ALERTS ({flags.length})</div>
                <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                  {flags.map((f, i) => (
                    <div key={i} style={{ padding:'4px 7px', background:'rgba(239,68,68,0.08)', borderLeft:'2px solid #ef4444', borderRadius:4 }}>
                      <div style={{ fontSize:10, color:'#fca5a5', fontWeight:600 }}>{f.reason}</div>
                      <div style={{ fontSize:9, color:'#475569' }}>{f.ts}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Mobile flags slide-up */}
      {isMobile && showFlags && flags.length > 0 && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'#0f172a', borderTop:'1px solid rgba(255,255,255,0.1)', maxHeight:200, overflow:'auto', padding:12, zIndex:100 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <span style={{ color:'#fca5a5', fontWeight:700, fontSize:12 }}>⚠️ Alerts ({flags.length})</span>
            <button onClick={() => setShowFlags(false)} style={{ color:'#64748b', background:'none', border:'none', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
          {flags.map((f, i) => (
            <div key={i} style={{ padding:'4px 7px', background:'rgba(239,68,68,0.08)', borderLeft:'2px solid #ef4444', borderRadius:4, marginBottom:4 }}>
              <div style={{ fontSize:10, color:'#fca5a5', fontWeight:600 }}>{f.reason}</div>
              <div style={{ fontSize:9, color:'#475569' }}>{f.ts}</div>
            </div>
          ))}
        </div>
      )}

      {/* Paper verification modal */}
      {paperPrompt && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:16 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, maxWidth:340, width:'100%', textAlign:'center', boxShadow:'0 30px 80px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
            <h3 style={{ fontWeight:800, fontSize:17, color:'#0f1f45', marginBottom:8 }}>Answer Sheet Check</h3>
            <p style={{ color:'#64748b', fontSize:13, lineHeight:1.6, marginBottom:20 }}>
              Please hold your answer sheet up to the camera briefly to confirm you are writing your answers on paper.
            </p>
            <button onClick={() => setPaperPrompt(false)}
              style={{ width:'100%', padding:'12px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer' }}>
              ✓ Showing my answer sheet
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
        html, body { overflow: hidden; height: 100%; }
      `}</style>
    </div>
  );
}
